/**
 * The authorization step on the real rail (GH-240, DEC-002).
 *
 * The server has created a PaymentIntent on the platform account; this screen is where the
 * donor authorizes it. Card entry happens inside Stripe's Payment Element — an iframe Stripe
 * hosts — so no digit of a PAN or CVV ever touches this bundle, our servers, or our logs
 * (SPEC.md §10.1). Wallets are deliberately *off* here: Apple Pay and Google Pay have their own
 * direct buttons on the payment sheet (`WalletExpress.tsx`), and a donor who tapped "Card"
 * asked for exactly the card form — re-offering the wallets made this the second of two
 * identical screens.
 *
 * Confirmation here is authorization only. The receipt screen that follows still waits for the
 * provider's webhook before saying "received" (SPEC.md §5.4) — a browser callback is a hint,
 * never the truth about money.
 */
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useDialogFocus } from "@givehere/ui";
import { Explain } from "./Explain.js";
import { donorMessageForStripeError } from "./card-failure-message.js";

const PUBLISHABLE_KEY: string = import.meta.env["VITE_STRIPE_PUBLISHABLE_KEY"] ?? "";

/** Loaded once per page, not per mount — Stripe.js is a singleton script. */
let stripePromise: Promise<Stripe | null> | null = null;
export const stripeClient = (): Promise<Stripe | null> => {
  if (!PUBLISHABLE_KEY) return Promise.resolve(null);
  stripePromise ??= loadStripe(PUBLISHABLE_KEY);
  return stripePromise;
};

export const stripeConfiguredInBundle = (): boolean => PUBLISHABLE_KEY.length > 0;

export function StripeConfirm({
  clientSecret,
  amountLabel,
  organizationDisplayName,
  onAuthorized,
  onCancel,
  aside = null,
}: {
  clientSecret: string;
  amountLabel: string;
  organizationDisplayName: string;
  /** Authorization finished (succeeded or processing). The receipt screen takes it from here. */
  onAuthorized: () => void;
  onCancel: () => void;
  /**
   * A line under the amount, above the card form: the give box's sign-in offer (GH-877), when its
   * widget shows the profile option and the donor is not signed in. Nothing otherwise.
   */
  aside?: ReactNode;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void stripeClient().then((client) => {
      if (cancelled || !client || !mountRef.current) return;
      const created = client.elements({
        clientSecret,
        appearance: { variables: { colorPrimary: "#0a6b4f", borderRadius: "14px" } },
      });
      const element = created.create("payment", {
        layout: "tabs",
        // The wallets have their own direct buttons on the sheet; this screen is the card form.
        wallets: { applePay: "never", googlePay: "never" },
      });
      element.on("ready", () => setReady(true));
      element.mount(mountRef.current);
      setStripe(client);
      setElements(created);
    });
    return () => {
      cancelled = true;
    };
  }, [clientSecret]);

  const confirm = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Some methods bounce through the bank (3-D Secure). The return lands back on this
        // checkout URL; Checkout.tsx recognises the redirect parameters and resumes on the
        // receipt screen.
        return_url: window.location.href,
      },
      // Cards and wallets usually complete in place; only methods that genuinely require a
      // redirect take one.
      redirect: "if_required",
    });
    if (result.error) {
      setError(donorMessageForStripeError(result.error));
      setBusy(false);
      return;
    }
    onAuthorized();
  };

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <div className="sheet__panel sheet__panel--tall" ref={panelRef} tabIndex={-1}>
        <button
          type="button"
          className="sheet__close"
          onClick={onCancel}
          aria-label="Go back"
          disabled={busy}
        >
          &#x2715;
        </button>

        <p className="sheet__amount" id="confirm-title">
          {amountLabel}
        </p>
        <p className="sheet__to">in support of {organizationDisplayName}</p>
        {aside}

        {!stripeConfiguredInBundle() && (
          <p className="notice notice--error" role="alert">
            Payments are not configured in this build. Please try again later.
          </p>
        )}

        {!ready && stripeConfiguredInBundle() && <p className="notice">Loading secure payment…</p>}

        <div ref={mountRef} className="stripe-element" />

        {error && (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        )}

        {ready && (
          <button type="button" className="pay" disabled={busy} onClick={() => void confirm()}>
            {busy ? "Processing…" : `Pay ${amountLabel}`}
          </button>
        )}

        <Explain
          className="sheet__foot"
          label="Processed securely by Stripe"
          title="Your card details"
        >
          <p>Card details never reach us.</p>
        </Explain>
      </div>
    </div>
  );
}
